/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\MonitoringNotificationsBox.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the system module.
----------------------------------------------------------------*/


import React from 'react';
import { HardDrive, Cpu, Mail, RefreshCw } from 'lucide-react';
import Toggle from '../../../components/ui/Toggle';
import SimpleTooltip from '../../../components/ui/SimpleTooltip';
import { FormBox } from '../../../components/ui/FormBox';
import { useI18n } from '../../../hooks/useI18n';
import type { SystemConfig } from '../../../types/system';
import type { ServerStats } from '../hooks/useServerStats';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface MonitoringNotificationsBoxProps {
  formData: SystemConfig;
  serverStats: ServerStats;
  onInputChange: (field: keyof SystemConfig, value: SystemConfig[keyof SystemConfig]) => void;
  onRefresh: () => void;
}

// ---------------------------------------------------------------------------
// DiskRow sub-component
// ---------------------------------------------------------------------------

interface DiskRowProps {
  /** Partition stats from the monitoring API */
  partition: ServerStats['storage'][number];
  /** Field keys for threshold and email in the form */
  fields: {
    threshold: keyof SystemConfig;
    email: keyof SystemConfig;
    enabled: keyof SystemConfig;
  };
  formData: SystemConfig;
  onInputChange: MonitoringNotificationsBoxProps['onInputChange'];
  emailPlaceholder: string;
  tooltipBarKey: string;
}

/**
 * DiskRow
 *
 * Renders one disk-partition row with a usage bar, threshold input,
 * alert email, and enable/disable toggle.
 */
function DiskRow({ partition, fields, formData, onInputChange, emailPlaceholder, tooltipBarKey }: DiskRowProps) {
  const { t } = useI18n();
  const isEnabled = !!formData[fields.enabled];

  return (
    <div className="border rounded-lg p-3">
      <div className="flex items-center gap-3">
        <HardDrive className="h-8 w-8 text-gray-400" />
        <div className="flex-1">
          <SimpleTooltip title="Disk Usage" content={t(tooltipBarKey)} position="top" maxWidth="300px">
            <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
              <div className="bg-green-500 h-2 rounded-full" style={{ width: `${partition.percentage}%` }} />
            </div>
          </SimpleTooltip>
          <div className="text-sm text-foreground">
            {partition.mountPoint} ? {partition.used} GB used of {partition.total} GB ({partition.percentage}%)
          </div>
        </div>
        <div className="flex items-center gap-2">
          <SimpleTooltip title="Threshold" content={t('systemMisc.tooltips.threshold')} position="top" maxWidth="300px">
            <input
              type="number"
              value={formData[fields.threshold] as number}
              onChange={(e) => onInputChange(fields.threshold, parseInt(e.target.value) || 70)}
              className="w-12 px-2 py-2 text-sm border border-border rounded bg-transparent text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              disabled={!isEnabled}
            />
          </SimpleTooltip>
          <SimpleTooltip title="Email" content={t('systemMisc.tooltips.email')} position="top" maxWidth="300px">
            <input
              type="email"
              placeholder={emailPlaceholder}
              value={formData[fields.email] as string}
              onChange={(e) => onInputChange(fields.email, e.target.value)}
              className="w-64 px-3 py-2 text-sm border border-border rounded bg-transparent text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
              disabled={!isEnabled}
            />
          </SimpleTooltip>
          <SimpleTooltip title="Enable/Disable" content={t('systemMisc.tooltips.toggleEnableDisable')} position="top" maxWidth="300px">
            <Toggle
              value={isEnabled}
              onChange={(value) => onInputChange(fields.enabled, value)}
            />
          </SimpleTooltip>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// MonitoringNotificationsBox
// ---------------------------------------------------------------------------

/**
 * MonitoringNotificationsBox
 *
 * Renders the "System Monitoring & Notifications" section of the System Config
 * form view. Shows disk usage bars and CPU usage with alert configuration.
 */
export function MonitoringNotificationsBox({
  formData,
  serverStats,
  onInputChange,
  onRefresh,
}: MonitoringNotificationsBoxProps) {
  const { t } = useI18n();
  const isCpuEnabled = !!formData.cpuNotifications;

  return (
    <FormBox title="System Monitoring & Notifications" icon={<Mail />}>
      {/* Refresh button */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <SimpleTooltip title="Refresh" content={t('systemMisc.tooltips.refreshButton')} position="top" maxWidth="300px">
            <button
              type="button"
              onClick={onRefresh}
              className="flex items-center gap-1 px-2 py-1 text-xs text-primary hover:text-primary/80 hover:bg-primary/10 rounded transition-colors border border-primary/20"
            >
              <RefreshCw className="h-3 w-3" />
              Refresh
            </button>
          </SimpleTooltip>
        </div>
      </div>

      <div className="space-y-3">
        {/* Storage Partition 1 � Root */}
        <DiskRow
          partition={serverStats.storage[0] ?? { filesystem: '', total: 0, used: 0, available: 0, percentage: 0, mountPoint: '/' }}
          fields={{ threshold: 'rootStorageThreshold', email: 'rootStorageEmail', enabled: 'rootStorageNotifications' }}
          formData={formData}
          onInputChange={onInputChange}
          emailPlaceholder="root@example.com"
          tooltipBarKey="systemMisc.tooltips.diskUsageBarRoot"
        />

        {/* Storage Partition 2 � Var / EFI */}
        <DiskRow
          partition={serverStats.storage[1] ?? { filesystem: '', total: 0, used: 0, available: 0, percentage: 0, mountPoint: '/boot/efi' }}
          fields={{ threshold: 'varStorageThreshold', email: 'varStorageEmail', enabled: 'varStorageNotifications' }}
          formData={formData}
          onInputChange={onInputChange}
          emailPlaceholder="var@example.com"
          tooltipBarKey="systemMisc.tooltips.diskUsageBarVar"
        />

        {/* CPU Notifications */}
        <div className="border rounded-lg p-3">
          <div className="flex items-center gap-3">
            <Cpu className="h-8 w-8 text-blue-500" />
            <div className="flex-1">
              <SimpleTooltip content={t('systemMisc.tooltips.cpuUsageBar')} position="top" maxWidth="300px">
                <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                  <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${serverStats.cpu.percentage}%` }} />
                </div>
              </SimpleTooltip>
              <div className="text-sm text-foreground">
                CPU Usage: {serverStats.cpu.percentage}%
              </div>
            </div>
            <div className="flex items-center gap-2">
              <SimpleTooltip title="Threshold" content={t('systemMisc.tooltips.threshold')} position="top" maxWidth="300px">
                <input
                  type="number"
                  value={formData.cpuThreshold}
                  onChange={(e) => onInputChange('cpuThreshold', parseInt(e.target.value) || 70)}
                  className="w-12 px-2 py-2 text-sm border border-border rounded bg-transparent text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  disabled={!isCpuEnabled}
                />
              </SimpleTooltip>
              <SimpleTooltip title="Email" content={t('systemMisc.tooltips.email')} position="top" maxWidth="300px">
                <input
                  type="email"
                  placeholder="cpu@example.com"
                  value={formData.cpuEmail}
                  onChange={(e) => onInputChange('cpuEmail', e.target.value)}
                  className="w-64 px-3 py-2 text-sm border border-border rounded bg-transparent text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
                  disabled={!isCpuEnabled}
                />
              </SimpleTooltip>
              <SimpleTooltip title="Enable/Disable" content={t('systemMisc.tooltips.toggleEnableDisable')} position="top" maxWidth="300px">
                <Toggle
                  value={isCpuEnabled}
                  onChange={(value) => onInputChange('cpuNotifications', value)}
                />
              </SimpleTooltip>
            </div>
          </div>
        </div>
      </div>
    </FormBox>
  );
}
